import React, { useState, useEffect } from 'react';
import {
  LuX, LuBuilding2, LuGlobe, LuLinkedin, LuMapPin,
  LuFileText, LuPencil, LuTrash, LuSave, LuUsers, LuUser,
  LuPhone, LuMail
} from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import { contactsService } from '../../services/contactsService';

const INDUSTRIES = [
  'Agriculture',
  'Cloud Services',
  'Consulting',
  'Education',
  'Energy',
  'Finance',
  'Food & Beverage',
  'Healthcare',
  'Hospitality',
  'Legal',
  'Manufacturing',
  'Marketing',
  'Media & Entertainment',
  'Nonprofit',
  'Real Estate',
  'Retail',
  'Software',
  'Technology',
  'Telecommunications',
  'Transportation & Logistics',
  'Other'
];

const AccountDetailsModal = ({ isOpen, onClose, account, onUpdate, onDelete, canDelete, loading }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);

  useEffect(() => {
    if (account) {
      setFormData({
        name: account.name || '',
        industry: account.industry || '',
        city: account.city || '',
        country: account.country || '',
        website: account.website || '',
        linkedin_url: account.linkedin_url || '',
        tax_id: account.tax_id || '',
        notes: account.notes || ''
      });
      setIsEditing(false);
      setIsEditing(false);
      setErrors({});
      fetchContacts(account.id);
    }
  }, [account, isOpen]);

  const fetchContacts = async (accountId) => {
    try {
      setLoadingContacts(true);
      const result = await contactsService.getContactsByAccount(accountId);
      if (result.success) {
        setContacts(result.data);
      }
    } catch (err) {
      console.error('Error fetching contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    }
    if (formData.website && !formData.website.startsWith('http')) {
      newErrors.website = 'URL must start with http:// or https://';
    }
    if (formData.linkedin_url && !formData.linkedin_url.includes('linkedin.com')) {
      newErrors.linkedin_url = 'Invalid LinkedIn URL';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    await onUpdate(account.id, formData);
    setIsEditing(false);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      onDelete(account.id);
    }
  };

  if (!isOpen || !account) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={LuBuilding2} className="h-6 w-6 text-blue-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditing ? 'Edit Account' : account.name}
                  </h3>
                  {!isEditing && account.industry && (
                    <p className="text-sm text-gray-500">{account.industry}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {!isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      disabled={loading}
                      className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-full hover:bg-blue-50 disabled:opacity-50"
                      title="Edit Account"
                    >
                      <SafeIcon icon={LuPencil} className="h-5 w-5" />
                    </button>
                    {canDelete && (
                      <button
                        type="button"
                        onClick={handleDelete}
                        disabled={loading}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-full hover:bg-red-50 disabled:opacity-50"
                        title="Delete Account"
                      >
                        <SafeIcon icon={LuTrash} className="h-5 w-5" />
                      </button>
                    )}
                  </>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-500 p-2"
                >
                  <SafeIcon icon={LuX} className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="px-4 py-5 sm:p-6">
            {isEditing ? (
              // Edit Form
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.name ? 'border-red-300' : 'border-gray-300'
                        }`}
                    />
                    {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select industry</option>
                      {INDUSTRIES.map((ind) => (
                        <option key={ind} value={ind}>{ind}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tax ID</label>
                    <input
                      type="text"
                      name="tax_id"
                      value={formData.tax_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.website ? 'border-red-300' : 'border-gray-300'
                        }`}
                    />
                    {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
                    <input
                      type="url"
                      name="linkedin_url"
                      value={formData.linkedin_url}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.linkedin_url ? 'border-red-300' : 'border-gray-300'
                        }`}
                    />
                    {errors.linkedin_url && <p className="mt-1 text-sm text-red-600">{errors.linkedin_url}</p>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ) : (
              // View Details
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Location & Tax */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Company Details</h4>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={LuMapPin} className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Location</p>
                        <p className="text-sm text-gray-600">
                          {account.city && account.country
                            ? `${account.city}, ${account.country}`
                            : account.city || account.country || 'Not specified'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={LuFileText} className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Tax ID</p>
                        <p className="text-sm text-gray-600">{account.tax_id || 'Not specified'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Digital Presence */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider">Digital Presence</h4>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={LuGlobe} className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Website</p>
                        {account.website ? (
                          <a href={account.website} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                            {account.website}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600">Not specified</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-start space-x-3">
                      <SafeIcon icon={LuLinkedin} className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">LinkedIn</p>
                        {account.linkedin_url ? (
                          <a href={account.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                            View Profile
                          </a>
                        ) : (
                          <p className="text-sm text-gray-600">Not specified</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {account.notes && (
                  <div className="pt-4 border-t border-gray-100">
                    <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Notes</h4>
                    <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
                      {account.notes}
                    </p>
                  </div>
                )}

                {/* Contacts Section */}
                <div className="pt-4 border-t border-gray-100">
                  <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
                    Contacts ({contacts.length})
                  </h4>

                  {loadingContacts ? (
                    <div className="text-center py-4 text-gray-500">Loading contacts...</div>
                  ) : contacts.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {contacts.map(contact => (
                        <div key={contact.id} className="bg-gray-50 p-3 rounded-lg flex items-start space-x-3">
                          <div className="flex-shrink-0 bg-white p-1.5 rounded-full border border-gray-200">
                            <SafeIcon icon={LuUser} className="h-4 w-4 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {contact.first_name} {contact.last_name}
                            </p>
                            {contact.job_title && (
                              <p className="text-xs text-gray-500 truncate">{contact.job_title}</p>
                            )}
                            <div className="mt-1 flex flex-col space-y-1">
                              {contact.email && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <SafeIcon icon={LuMail} className="h-3 w-3 mr-1" />
                                  <span className="truncate">{contact.email}</span>
                                </div>
                              )}
                              {contact.phone && (
                                <div className="flex items-center text-xs text-gray-500">
                                  <SafeIcon icon={LuPhone} className="h-3 w-3 mr-1" />
                                  <span className="truncate">{contact.phone}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No contacts associated with this account.</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
            {isEditing ? (
              <>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <SafeIcon icon={LuSave} className="h-4 w-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  disabled={loading}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm flex items-center disabled:opacity-50"
                >
                  <SafeIcon icon={LuX} className="h-4 w-4 mr-2" />
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default AccountDetailsModal;